import React, { useCallback, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { isDatasetIdentifier } from "utils/utils";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import useChartDialogStore from "store/chart-dialog-store";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { T } from "services/lang/LanguageService";

type DatasetProps = {
  values: string | string[] | null;
  changeHandler: (receivedValue: string[] | null) => void;
  isInEditMode: boolean;
  decimalPlaces?: number;
};

const Dataset: React.FC<DatasetProps> = ({
  changeHandler,
  values,
  isInEditMode,
  decimalPlaces,
}) => {
  const getCurrentValues = () => {
    if (typeof values === "string") {
      return [values];
    }
    return values;
  };
  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentDatasets, setCurrentDatasets] = useState<string[]>(getCurrentValues() || []);

  const handleDatasetsChange = useCallback((newDatasets: string[]) => {
    setCurrentDatasets(newDatasets);
  }, []);

  const handleDatasetsSave = useCallback(() => {
    changeHandler(currentDatasets);
    setDialogVisible(false);
  }, [changeHandler, currentDatasets]);

  const dialogFooterTemplate = useCallback(() => {
    return (
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={() => setDialogVisible(false)}
          className="component-editor-button icon-red"
        />
        <Button
          type="button"
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleDatasetsSave}
          className="component-editor-button icon-green"
        />
      </div>
    );
  }, [handleDatasetsSave]);

  if (isInEditMode) {
    return (
      <>
        <Button
          label={T("word.datasets")}
          className="bg-gray-200 border-none text-black-alpha-90 hover:bg-gray-300 h-2rem"
          icon="pi pi-list"
          onClick={() => setDialogVisible(true)}
        />
        <Dialog
          header={T("action.select-datasets")}
          visible={dialogVisible}
          style={{ width: "40rem" }}
          footer={dialogFooterTemplate}
          modal
          onHide={() => setDialogVisible(false)}
        >
          <DatasetMultiSelect value={currentDatasets} onChange={handleDatasetsChange} />
        </Dialog>
      </>
    );
  }

  const getNormalizedValues = () => {
    if (Array.isArray(values)) {
      return values;
    }
    return values ? [values] : [];
  };

  const datasets = getNormalizedValues().map(value => (isDatasetIdentifier(value) ? value : null));

  if (datasets.length === 0) return "-";

  return (
    <div>
      {datasets?.map(
        dataset =>
          dataset && (
            <DatasetDisplay key={dataset} identifier={dataset} decimalPlaces={decimalPlaces} />
          )
      )}
    </div>
  );
};

const DatasetDisplay = ({
  identifier,
  decimalPlaces,
}: {
  identifier: string;
  decimalPlaces?: number;
}) => {
  const selectedIdsStore = useDatasetIdentifiersStore();
  const { openDialog } = useChartDialogStore();
  const { data, isLoading } = useGetRecentData([identifier!], {
    options: { enabled: !!identifier },
  });

  const value = data?.[0].data?.[0]?.value;
  const unit = data?.[0].unit;

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey) {
      if (selectedIdsStore.ids.has(identifier)) {
        selectedIdsStore.removeIdentifier(identifier);
      } else {
        selectedIdsStore.addIdentifier(identifier);
      }
    } else {
      selectedIdsStore.clear();
    }
  };

  const handleDoubleClick = () => {
    selectedIdsStore.addIdentifier(identifier);
    openDialog();
  };

  const isIdentifierSelected = selectedIdsStore.ids.has(identifier);

  return (
    <div
      className="p-inputtext"
      style={{
        border: isIdentifierSelected ? "1px solid #007df2" : "none",
        borderRadius: "0px",
        backgroundColor: "inherit",
        color: "inherit",
        whiteSpace: "pre-wrap",
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {isLoading && <div className="pi pi-spin pi-spinner" />}
      {!value && !isLoading && <>&#8208;</>}
      {value?.toFixed(decimalPlaces ?? 2)}
      {unit}
    </div>
  );
};

export default Dataset;
